import React, { useEffect } from "react";
import classNames from "classnames";
import _ from "lodash";
import { connect } from "react-redux";

import { Checkbox, EmptyState, Tooltip } from "@toddle-edu/ds-web";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import MessagingAvatar from "MessagingV2/components/ThreadList/Avatar";
import useI18n from "Hooks/UseI18n";
import {
  getFormattedNameForUserType,
  getSubText,
} from "MessagingV2/modules/Utils";
import { getDefaultNameSortPreference } from "modules/CommonSelectors";
import { NoSearchInFilterIllustration } from "@toddle-edu/ds-theme";

import classes from "./MemberList.scss";

const SUB_TEXT_LENGTH = 40;

const MemberCard = ({
  avatarId,
  mainText,
  subText,
  profileImage,
  isSelected,
  onToggleSelection,
}) => {
  return (
    <div className={classes.userTypeCardContainer}>
      <MessagingAvatar
        id={avatarId}
        name={mainText}
        url={profileImage}
        onlineStatus={false}
      />
      <div className={classes.userInfoContainer}>
        <div className="text-body">{mainText}</div>
        {subText ? (
          <ConditionalWrapper
            wrapper={<Tooltip tooltip={subText} />}
            condition={_.size(subText) > SUB_TEXT_LENGTH}
            type={"wrapperAsElement"}
          >
            <div className={classNames("text-body-s", classes.userSubTitle)}>
              {_.truncate(subText, { length: SUB_TEXT_LENGTH })}
            </div>
          </ConditionalWrapper>
        ) : null}
      </div>
      <div className={classes.iconButtonContainer}>
        <Checkbox
          onChange={onToggleSelection}
          isChecked={isSelected}
          dsVersion="2.0"
        />
      </div>
    </div>
  );
};

const MemberList = props => {
  const { t } = useI18n(["common"]);

  const {
    users,
    defaultStudentNameSortPreference,
    handleAddUsers,
    handleRemoveUsers,
    selectedUsers,
    allSelected,
    isUpdating,
  } = props;

  useEffect(() => {
    if (allSelected && !isUpdating) {
      const usersToAdd = _.map(users, ({ node }) => node);
      handleAddUsers(usersToAdd);
    }
  }, [allSelected, handleAddUsers, users, isUpdating]);

  if (_.isEmpty(users)) {
    return (
      <EmptyState
        dsVersion="2.0"
        illustration={NoSearchInFilterIllustration}
        subtitle={t("common:no_family_members")}
      />
    );
  }

  return _.map(users, ({ node: user }) => {
    const id = _.get(user, "id");
    const profileImage = _.get(user, "profileImage");
    const name = getFormattedNameForUserType({
      user,
      orderBy: defaultStudentNameSortPreference,
      isFormattedNameV2: true,
    });

    const isSelected = _.some(selectedUsers, { id });
    const onToggleSelection = () => {
      if (isSelected) {
        handleRemoveUsers([user]);
      } else {
        handleAddUsers([user]);
      }
    };

    return (
      <MemberCard
        key={id}
        avatarId={id}
        mainText={name}
        subText={getSubText({
          messagedUserObj: user,
          messagedUserRole: _.get(user, "type"),
          t,
          defaultStudentNameSortPreference,
        })}
        profileImage={profileImage}
        isSelected={isSelected}
        onToggleSelection={onToggleSelection}
      />
    );
  });
};

const mapStateToProps = state => {
  const defaultStudentNameSortPreference = getDefaultNameSortPreference(state);
  return {
    defaultStudentNameSortPreference,
  };
};

export default connect(mapStateToProps)(MemberList);
